// Single source of truth for the deal documents.
// Update these paths whenever a document is regenerated.
export const SITE = "https://assurance.colosseumlegacy.com";

export const PROSPECTUS_PDF = "/Colosseum_SportClips_Pitch_c3f9a7.pdf";
export const PROSPECTUS_DOCX = "/Colosseum_SportClips_Pitch_c3f9a7.docx";
export const PROSPECTUS_HTML = "/Colosseum_SportClips_Pitch_c3f9a7.html";
export const PROSPECTUS_TITLE = "BCS Seat-to-Sale — Prepared for Sport Clips";

export const PILOT_PDF = "/Colosseum_SportClips_Pilot_e8b1d5.pdf";
export const PILOT_DOCX = "/Colosseum_SportClips_Pilot_e8b1d5.docx";
export const PILOT_HTML = "/Colosseum_SportClips_Pilot_e8b1d5.html";

export const TECH_PDF = "/Colosseum_SportClips_Tech_f2c6a9.pdf";
export const TECH_DOCX = "/Colosseum_SportClips_Tech_f2c6a9.docx";
export const TECH_HTML = "/Colosseum_SportClips_Tech_f2c6a9.html";
export const TECH_TITLE = "BCS Seat-to-Sale — Platform & Technical Breakdown";

// Confidential partner-conversation docs. Kept OUT of the Documents portal until
// PRIVATE_DOCS_ENABLED is flipped to true (the "trigger").
export const PRIVATE_DOCS_ENABLED = false;
export const TITAN_PDF = "/Colosseum_Global_Licensing_Titan_b7e3f1.pdf";
export const TITAN_DOCX = "/Colosseum_Global_Licensing_Titan_b7e3f1.docx";
export const TITAN_TITLE = "Global Licensing Opportunity — Titan View";
export const BRIEF_PDF = "/Colosseum_Target_Companies_Brief_a5d8c2.pdf";
export const BRIEF_DOCX = "/Colosseum_Target_Companies_Brief_a5d8c2.docx";
export const BRIEF_TITLE = "Sector Targets & Points of Contact";
export const PILOT_TITLE = "BCS Seat-to-Sale — Pilot at a Glance";

// Builds a mailto: link so a recipient can forward the document internally in one click.
export function shareMailto(title, path) {
  const url = `${SITE}${path}`;
  const subject = encodeURIComponent(`${title} — Colosseum Legacy`);
  const body = encodeURIComponent(
    `Hi,\n\nPlease find the ${title} from Colosseum Legacy (Revenue Assurance) below.\n\n` +
    `Document: ${url}\nLive platform: ${SITE}\n\n` +
    `Feel free to forward this internally.\n\nBest regards,`
  );
  return `mailto:?subject=${subject}&body=${body}`;
}

// Opens the phone's native share/message sheet where available (mobile),
// falling back to an email draft on desktop browsers without Web Share.
export async function shareDoc(title, path) {
  const url = `${SITE}${path}`;
  if (typeof navigator !== "undefined" && navigator.share) {
    try {
      await navigator.share({ title, text: `${title} — Colosseum Legacy`, url });
      return;
    } catch (err) {
      if (err && err.name === "AbortError") return;
    }
  }
  window.location.href = shareMailto(title, path);
}

// ── Chatters pitch pack (Canadian target) ───────────────────────────────────
export const CHATTERS_PITCH_PDF = "/Colosseum_Chatters_Pitch_d4a1b8.pdf";
export const CHATTERS_PITCH_DOCX = "/Colosseum_Chatters_Pitch_d4a1b8.docx";
export const CHATTERS_PITCH_HTML = "/Colosseum_Chatters_Pitch_d4a1b8.html";
export const CHATTERS_PITCH_TITLE = "BCS Seat-to-Sale — Prepared for Chatters";

// ── New Zealand pitch pack ───────────────────────────────────────────────────
export const NZ_PITCH_PDF = "/Colosseum_NewZealand_Pitch_a7c2.pdf";
export const NZ_PITCH_DOCX = "/Colosseum_NewZealand_Pitch_a7c2.docx";
export const NZ_PITCH_HTML = "/Colosseum_NewZealand_Pitch_a7c2.html";
export const NZ_PITCH_TITLE = "BCS Seat-to-Sale — Prepared for New Zealand";

// ── Just Cuts joint-venture pitch pack (Australia + Canada) ──────────────────
export const JUSTCUTS_AU_PDF = "/Colosseum_JustCuts_Australia_Pitch_au7d3c.pdf";
export const JUSTCUTS_AU_DOCX = "/Colosseum_JustCuts_Australia_Pitch_au7d3c.docx";
export const JUSTCUTS_AU_HTML = "/Colosseum_JustCuts_Australia_Pitch_au7d3c.html";
export const JUSTCUTS_AU_TITLE = "BCS Seat-to-Sale — Just Cuts Australia (JV Proposal)";
export const JUSTCUTS_CA_PDF = "/Colosseum_JustCuts_Canada_Pitch_ca9f21.pdf";
export const JUSTCUTS_CA_DOCX = "/Colosseum_JustCuts_Canada_Pitch_ca9f21.docx";
export const JUSTCUTS_CA_HTML = "/Colosseum_JustCuts_Canada_Pitch_ca9f21.html";
export const JUSTCUTS_CA_TITLE = "BCS Seat-to-Sale — Just Cuts Canada (JV Proposal)";

// Returns the target-specific document set + labelling for the Documents page.
export function docSetFor(target) {
  if (target === "justcuts") {
    return {
      label: "Just Cuts",
      footprint: "The Ontario launch cohort — Just Cuts Canada",
      prospectus: {
        pdf: JUSTCUTS_CA_PDF, docx: JUSTCUTS_CA_DOCX, html: JUSTCUTS_CA_HTML, title: JUSTCUTS_CA_TITLE,
        smallLabel: "Master Prospectus · Canada",
        desc: "The Canadian joint-venture proposal, built around the Ontario launch cohort (Guelph · London · Toronto): founder letter, patent status, the proposed 50/50 North American framework, our charitable mandate, and the technical blueprint.",
      },
      prospectus2: null,
      pilot: null,
      tech: { pdf: TECH_PDF, docx: TECH_DOCX, html: TECH_HTML, title: TECH_TITLE },
    };
  }
  if (target === "chatters") {
    return {
      label: "Chatters",
      footprint: "~116 Canadian salons (~84 corporate, ~32 franchise)",
      prospectus: { pdf: CHATTERS_PITCH_PDF, docx: CHATTERS_PITCH_DOCX, html: CHATTERS_PITCH_HTML, title: CHATTERS_PITCH_TITLE },
      pilot: null,
      tech: { pdf: TECH_PDF, docx: TECH_DOCX, html: TECH_HTML, title: TECH_TITLE },
    };
  }
  if (target === "newzealand") {
    return {
      label: "New Zealand",
      footprint: "~60 stores across NZ's 5 largest hair & barber franchise brands",
      prospectus: { pdf: NZ_PITCH_PDF, docx: NZ_PITCH_DOCX, html: NZ_PITCH_HTML, title: NZ_PITCH_TITLE },
      pilot: null,
      tech: { pdf: TECH_PDF, docx: TECH_DOCX, html: TECH_HTML, title: TECH_TITLE },
    };
  }
  return {
    label: "Sport Clips",
    footprint: "~1,900 US & ~35 Canada locations",
    prospectus: { pdf: PROSPECTUS_PDF, docx: PROSPECTUS_DOCX, html: PROSPECTUS_HTML, title: PROSPECTUS_TITLE },
    pilot: { pdf: PILOT_PDF, docx: PILOT_DOCX, html: PILOT_HTML, title: PILOT_TITLE },
    tech: { pdf: TECH_PDF, docx: TECH_DOCX, html: TECH_HTML, title: TECH_TITLE },
  };
}
